import { Injectable, inject } from '@angular/core';
import { APIService } from 'src/app/services/api.service';
import format from 'date-fns/format';
import { addDays, addWeeks, addMonths, addYears, isBefore, isAfter, startOfWeek } from 'date-fns';
import { LoggingService } from 'src/app/services/logging.service';
import { EventComponent } from './event.component';
import { environment } from '../../../../environments/environment';
import { FormInfos } from 'src/app/components/forms-explorer/forms-explorer.component';
import { UserService } from 'src/app/services/user.service';
import { User } from '../../users/models/user.model';
import { ContactsService } from '../../contacts/contacts.service';
import { take } from 'rxjs';
import { AmplifyApiService } from 'src/app/services/amplify-api.service';

export interface dayOfWeek {
  selected: boolean;
  day: string;
  name: string;
  index: number;
}

export interface Event {
  _id?: string;
  title: string;
  title_fr: string;
  title_es: string;
  description: string;
  description_fr: string;
  description_es: string;
  eventDate: Date;
  from: string;
  to: Array<string>;
  users?: Array<any>;
  groups?: Array<string>;
  selectedProgramId: string[];
  type: string;
  repeat: string;
  repeatUntil: any;
  forms: FormInfos[];
  lastModified?: number;
  parentEvent?: string;
  repeatInformation?: {
    repeatEveryXWeek: number;
    repeatEveryXMonth: number;
    daysOfWeek: Array<dayOfWeek>;
  };
  predio?: string;
  predioLink?: string;
}

@Injectable({
  providedIn: 'root',
})
export class EventService {
  private readonly COLLECTION_NAME = 'schedules';
  private contactRoles: string[] = [];
  public eventComponent!: EventComponent;

  constructor(
    private readonly apiService: APIService,
    private readonly logger: LoggingService,
    private readonly userService: UserService,
    private readonly contactsService: ContactsService,
  ) {
    this.contactsService.currentContact$.pipe(take(1)).subscribe({
      next: (contact) => {
        if (contact) {
          this.contactRoles = contact.contactRoles;
        }
      },
      error: (error) => {
        this.logger.error(error);
      }
    });
  }

  public async getEventsForYear(year: number, filters: unknown): Promise<Array<Event>> {
    return this.API.get(
      environment.API_NAME,
      `/v2/events?filters=${encodeURIComponent(JSON.stringify(filters))}&for=year&year=${year}`,
      {},
    );
  }

  public async getCountForToday(): Promise<number> {
    const email = (this.apiService.user as any).attributes.email;

    if (email && this.contactRoles) {
      return this.API.get(
        environment.API_NAME,
        `/v2/events?for=day&count=true&email=${encodeURIComponent(email)}&groups=${JSON.stringify(this.contactRoles)}`,
        {},
      );
    } else {
      return this.API.get(environment.API_NAME, `/v2/events?for=day&count=true&email=${encodeURIComponent(email)}`, {});
    }
  }

  public async createEvent(event: Event): Promise<Event> {
    const clone = { ...event };
    delete clone._id;

    this.logger.debug('createEvent', clone);

    return this.API.post(environment.API_NAME, '/v2/events', { body: clone });
  }

  public async updateEvent(event: Event): Promise<Event> {
    this.logger.debug('updateEvent', event);
    return this.API.patch(environment.API_NAME, `/v2/events/${event._id}`, { body: event });
  }

  public async deleteEvent(id: string | undefined): Promise<void> {
    if (!id) {
      throw new Error('No id provided');
    }

    this.logger.debug('deleteEvent', id);
    await this.API.del(environment.API_NAME, `/v2/events/${id}`, {});
  }

  public async createManyEvents(events: Array<Event>): Promise<Event> {
    const eventsToBeCreated = events.map((event: any) => {
      const clone = { ...event };
      delete clone._id;

      return clone;
    });

    this.logger.debug('createManyEvents', eventsToBeCreated);
    return this.API.post(environment.API_NAME, '/v2/events', { body: eventsToBeCreated });
  }

  public async updateManyEvents(filter: any[], fieldsToUpdate: any[]): Promise<Event> {
    const body = {
      filter,
      fieldsToUpdate,
    };
    this.logger.debug('updateManyEvents', body);
    return this.API.patch(environment.API_NAME, `/v2/events`, { body });
  }

  public async deleteManyEvents(ids: string[]): Promise<void> {
    this.logger.debug('deleteManyEvents');
    await this.API.del(environment.API_NAME, `/v2/events?ids=${JSON.stringify(ids)}`, {});
  }

  public buildFromFormEvent(userEmail: string, selectedPrograms: string[]): Event {
    const formEvent = {
      ...this.eventComponent.eventForm.getRawValue(),
    };

    if (formEvent.repeat !== 'never') {
      this.eventComponent.currentEvent.eventDate = formEvent.eventDate;
      this.eventComponent.currentEvent.repeatUntil = formEvent.repeatUntil;
    }

    const to = [];
    if (this.eventComponent.toMyself) {
      to.push(userEmail);
    }
    if (formEvent.users) {
      to.push(
        ...formEvent.users.map((x: User) => {
          return this.userService.getUserAttribute(x, 'email');
        }),
      );
    }
    if (formEvent.groups) {
      to.push(...formEvent.groups);
    }
    if (formEvent.forms) {
      formEvent.forms.forEach((x: FormInfos) => {
        delete x.form;
        delete x.formDef;
        delete x.descriptor;
      });
    }

    return {
      _id: this.eventComponent.eventId,
      title: formEvent.title,
      title_fr: formEvent.title_fr,
      title_es: formEvent.title_es,
      description: formEvent.description,
      description_fr: formEvent.description_fr,
      description_es: formEvent.description_es,
      eventDate: this.setHourToMidnight(formEvent.eventDate),
      from: userEmail,
      to,
      users: formEvent.users,
      groups: formEvent.groups,
      selectedProgramId: selectedPrograms,
      forms: formEvent.forms ?? [],
      type: '',
      repeat: formEvent.repeat,
      repeatUntil: formEvent.repeat !== 'never' ? this.setHourToMidnight(formEvent.repeatUntil) : undefined,
      parentEvent: this.eventComponent.currentEvent.parentEvent,
      lastModified: this.eventComponent.currentEvent.lastModified,
      repeatInformation: this.eventComponent.currentEvent.repeatInformation,
    };
  }

  private prepare(day: Date, userEmail: string, selectedPrograms: string[]): Event {
    const to = [];
    if (this.eventComponent.toMyself) {
      to.push(userEmail);
    }
    if (this.eventComponent.eventForm.get('users')?.value) {
      to.push(
        ...(this.eventComponent.eventForm.get('users')?.value.map((x: User) => {
          return this.userService.getUserAttribute(x, 'email');
        }) || []),
      );
    }
    const group = this.eventComponent.eventForm.get('groups');
    if (group?.value) {
      to.push(...group.value);
    }

    return {
      title: this.eventComponent.eventForm.get('title')?.value,
      title_fr: this.eventComponent.eventForm.get('title_fr')?.value,
      title_es: this.eventComponent.eventForm.get('title_es')?.value,
      description: this.eventComponent.eventForm.get('description')?.value,
      description_fr: this.eventComponent.eventForm.get('description_fr')?.value,
      description_es: this.eventComponent.eventForm.get('description_es')?.value,
      eventDate: day,
      type: '',
      repeat: this.eventComponent.eventForm.get('repeat')?.value,
      repeatUntil: this.eventComponent.eventForm.get('repeatUntil')?.value,
      from: userEmail,
      forms: this.eventComponent.eventForm.get('forms')?.value,
      to,
      users: this.eventComponent.eventForm.get('users')?.value,
      groups: this.eventComponent.eventForm.get('groups')?.value,
      selectedProgramId: selectedPrograms,
      parentEvent: this.eventComponent.currentEvent.parentEvent || this.eventComponent.eventId,
      repeatInformation: this.eventComponent.currentEvent.repeatInformation,
    };
  }

  public async saveItemAndChildEvents(
    event: Event,
    originalForm: Event,
    userEmail: string,
    selectedPrograms: string[],
  ): Promise<void> {
    try {
      const masterId = event.parentEvent ? event.parentEvent : this.eventComponent.eventId;
      let masterEvent = await this.apiService.getItem(this.COLLECTION_NAME, masterId);
      masterEvent = this.updateModifiedFields(masterEvent, event);
      await this.updateEvent(masterEvent);

      if (originalForm.repeat === 'never') {
        await this.createChildEvents(userEmail, selectedPrograms);
        this.logger.debug(`Created child events for ${this.eventComponent.eventId}`);
      } else {
        await this.deleteChildEvents();
        await this.createChildEvents(userEmail, selectedPrograms);
      }
    } catch (error) {
      this.logger.error(error);
    }
  }

  private updateModifiedFields(masterEvent: Event, modifiedEvent: Event): any {
    masterEvent.title = modifiedEvent.title;
    masterEvent.users = modifiedEvent.users;
    masterEvent.groups = modifiedEvent.groups;
    masterEvent.repeat = modifiedEvent.repeat;
    masterEvent.repeatUntil = modifiedEvent.repeatUntil;
    masterEvent.description = modifiedEvent.description;

    return masterEvent;
  }

  public computeRepeatUntil(value: string): Date {
    let today;
    let repeatUntil = new Date();
    const chosenDate = new Date(this.eventComponent.eventForm.get('eventDate')?.value);

    switch (value) {
      case 'every_day':
        repeatUntil = addMonths(chosenDate, 1);
        break;
      case 'every_week':
        today = this.eventComponent.currentEvent.repeatInformation?.daysOfWeek.find(
          (x: { index: number }) => x.index === chosenDate.getDay(),
        );
        if (today) {
          today.selected = true;
        }
        repeatUntil = addMonths(chosenDate, 6);
        break;
      case 'every_month':
        repeatUntil = addMonths(chosenDate, 12);
        break;
      case 'every_year':
        repeatUntil = addYears(chosenDate, 10);
    }

    return repeatUntil;
  }

  public computeRepeat(): string {
    let repeat = '';
    const date = new Date(this.eventComponent.eventForm.get('repeatUntil')?.value);

    if (this.eventComponent.currentEvent.repeat === 'every_day') {
      repeat = $localize`day`;
    } else if (this.eventComponent.currentEvent.repeat === 'every_week') {
      const selectedDays = this.eventComponent.currentEvent.repeatInformation?.daysOfWeek
        .filter((day: { selected: any }) => day.selected)
        .map((x: { name: any }) => x.name)
        .join(', ');

      if (this.eventComponent.currentEvent.repeatInformation?.repeatEveryXWeek === 1) {
        repeat = $localize`week, on ${selectedDays}`;
      } else {
        repeat = $localize`${this.eventComponent.currentEvent.repeatInformation?.repeatEveryXWeek} weeks, on ${selectedDays}`;
      }
    } else if (this.eventComponent.currentEvent.repeat === 'every_month') {
      if (this.eventComponent.currentEvent.repeatInformation?.repeatEveryXWeek === 1) {
        repeat = $localize`month, on day ${date.getDate()}`;
      } else {
        repeat = $localize`${this.eventComponent.currentEvent.repeatInformation
          ?.repeatEveryXMonth} month, on day ${date.getDate()}`;
      }
    } else if (this.eventComponent.currentEvent.repeat === 'every_year') {
      repeat = $localize`year, on ${format(date, 'MMMM dd')}`;
    }
    return repeat;
  }

  public async createChildEvents(userEmail: string, selectedPrograms: string[]): Promise<void> {
    try {
      let events: Array<Event> = [];
      let eventDate;
      if (this.eventComponent.currentEvent.parentEvent) {
        const parent = await this.apiService.getItem(
          this.COLLECTION_NAME,
          this.eventComponent.currentEvent.parentEvent,
        );
        eventDate = this.setHourToMidnight(parent.eventDate);
      } else {
        eventDate = this.setHourToMidnight(this.eventComponent.currentEvent.eventDate);
      }
      const end = addDays(this.setHourToMidnight(this.eventComponent.currentEvent.repeatUntil), 1);
      const days = [];

      if (this.eventComponent.currentEvent.repeat === 'every_day') {
        for (let start = addDays(eventDate, 1); isBefore(start, end); start = addDays(start, 1)) {
          days.push(new Date(start));
        }
      } else if (this.eventComponent.currentEvent.repeat === 'every_week') {
        const xWeeks = this.eventComponent.currentEvent.repeatInformation?.repeatEveryXWeek || 1;
        for (let week = addDays(eventDate, 1); isBefore(week, addWeeks(end, 1)); week = addWeeks(week, xWeeks)) {
          for (let day = startOfWeek(week); day.getDay() < 6; day = addDays(day, 1)) {
            if (
              isAfter(day, eventDate) &&
              isBefore(day, end) &&
              this.eventComponent.currentEvent.repeatInformation?.daysOfWeek.find(
                (x: { index: number }) => x.index === day.getDay(),
              )?.selected
            ) {
              days.push(new Date(day));
            }
          }
        }
      } else if (this.eventComponent.currentEvent.repeat === 'every_month') {
        const xMonths = this.eventComponent.currentEvent.repeatInformation?.repeatEveryXMonth || 1;
        let i = 1;
        for (let start = addMonths(eventDate, xMonths); isBefore(start, end); start = addMonths(start, xMonths)) {
          days.push(addMonths(eventDate, xMonths * i++));
        }
      } else if (this.eventComponent.currentEvent.repeat === 'every_year') {
        for (let start = addYears(eventDate, 1); isBefore(start, end); start = addYears(start, 1)) {
          days.push(new Date(start));
        }
      }

      events = days.map((day) => {
        return this.prepare(day, userEmail, selectedPrograms);
      });

      await this.createManyEvents(events);
    } catch (error) {
      this.logger.error(error);
    }
  }

  public async deleteChildEvents(): Promise<void> {
    try {
      const filters = [
        {
          field: 'parentEvent',
          operator: 'eq',
          value: this.eventComponent.currentEvent.parentEvent || this.eventComponent.eventId,
        },
      ];
      const childEvents = await this.apiService.getCollection({
        collection: this.COLLECTION_NAME,
        filters,
      });

      await this.deleteManyEvents(childEvents.map((x: Event) => x._id));
      this.logger.debug('Deleted all child events');
    } catch (error) {
      this.logger.error(error);
    }
  }

  public async deleteItemAndChildEvents(): Promise<void> {
    try {
      const filter = [
        {
          field: 'or',
          value: [
            {
              field: '_id',
              operator: 'eq',
              value: this.eventComponent.currentEvent.parentEvent || this.eventComponent.currentEvent._id,
            },
            {
              field: 'parentEvent',
              operator: 'eq',
              value: this.eventComponent.currentEvent.parentEvent || this.eventComponent.currentEvent._id,
            },
          ],
        },
      ];

      const events = await this.apiService.getCollection({
        collection: this.COLLECTION_NAME,
        filters: filter,
      });
      const ids = events.map((event: { _id: any }) => event._id);
      await this.deleteManyEvents(ids);

      this.logger.debug('deleted the item and all child events');
    } catch (error) {
      this.logger.error(error);
    }
  }

  private setHourToMidnight(date: any): Date {
    const realDate = new Date(date);
    return new Date(realDate.setHours(0, 0, 0, 0));
  }

  private readonly API = inject(AmplifyApiService);
}
